import { useEffect, useMemo } from 'react';
import useLocalStorageState from '../../../utils/useLocalStorageState';
import useGetDivisions from '../useGetDivisions';
import {
    FILTER_TYPES,
    isAppliedFilterValue,
    renderAppliedFiltersDescription,
} from '../../../utils/filterUtils';
import { NEEDS_REVIEW_REASONS, PROCESSED_TIMESHEET_STATUSES } from '../../constants';
import StatusPill from '../../../Shared/Components/StatusPill';

const reviewReasonsOptions = Object.values(NEEDS_REVIEW_REASONS).map(({ label, name }) => ({
    label,
    value: name,
}));

const getDefaultStatuses = (storageKey) => {
    if (storageKey === 'processedTimesheetsBP') return ['BPReview', 'BPQuestion'];
    if (storageKey === 'processedTimesheetsTC') return ['TCReview', 'TCHold'];
    return [];
};

const filterStatusList = { ...PROCESSED_TIMESHEET_STATUSES };
delete filterStatusList.Creating;
delete filterStatusList.NeedsReview;

const useProcessedTimeseetsFilters = (storageKey = '') => {
    const { data: divisions, isLoading: isLoadingDivisions } = useGetDivisions();
    const divisionsNames = useMemo(() => divisions?.map((d) => d.description), [divisions]);
    const { state: filters, setValue: setFilters } = useLocalStorageState(`${storageKey}Filters`);
    const handleApplyFilter = (newFilters) => setFilters(newFilters);

    const filtersSetup = useMemo(
        () => [
            {
                name: 'statuses',
                label: 'Status',
                type: FILTER_TYPES.CHECKBOX,
                defaultValue: getDefaultStatuses(storageKey),
                overrides: {
                    options: Object.keys(filterStatusList),
                    render: (value) => <StatusPill status={value} />,
                },
                isQuickFilter: storageKey === 'processedTimesheetsBP',
            },
            {
                name: 'reviewReasons',
                label: 'Review Reasons',
                type: FILTER_TYPES.CHECKBOX,
                defaultValue: [],
                overrides: { options: reviewReasonsOptions },
                isQuickFilter: storageKey === 'processedTimesheetsTC',
            },
            {
                name: 'divisions',
                label: 'Divisions',
                type: FILTER_TYPES.CHECKBOX,
                defaultValue: [],
                overrides: { options: divisionsNames, emptyLabel: 'All' },
            },
            {
                name: 'payrollDate',
                label: 'Payroll Date',
                type: FILTER_TYPES.DATE_RANGE,
                defaultValue: { from: null, to: null },
            },
            {
                name: 'weekEndingDate',
                label: 'Week Ending Date',
                type: FILTER_TYPES.DATE_RANGE,
                defaultValue: { from: null, to: null },
            },
            {
                name: 'monthPosted',
                label: 'Month Posted',
                type: FILTER_TYPES.DATE_RANGE,
                defaultValue: { from: null, to: null },
            },
            {
                name: 'CorrectionsOnly',
                label: 'Flags',
                type: FILTER_TYPES.CHECKBOX,
                defaultValue: [],
                overrides: {
                    options: [{ label: 'Correction', value: 'true' }],
                    emptyLabel: 'All',
                },
            },
        ],
        [divisionsNames]
    );

    useEffect(() => {
        if (filters) return;
        // If filters are not set, set them to the default values
        const defaultFiltersValue = {};
        filtersSetup.forEach(({ name, defaultValue }) => {
            if (isAppliedFilterValue(defaultValue)) {
                defaultFiltersValue[name] = defaultValue;
            }
        });
        if (Object.keys(defaultFiltersValue).length > 0) {
            setFilters(defaultFiltersValue);
        }
    }, [filtersSetup]);

    const appliedFiltersDescription = useMemo(
        () => renderAppliedFiltersDescription(filters, filtersSetup),
        [filters, filtersSetup]
    );

    if (!storageKey) {
        throw new Error(
            'Storage key is required and should be unique to prevent overwriting data.'
        );
    }

    return {
        filters,
        setFilters,
        filtersSetup,
        handleApplyFilter,
        isLoadingFilterConfig: isLoadingDivisions,
        appliedFiltersDescription,
    };
};

export default useProcessedTimeseetsFilters;
